import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Image,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import { sessionDataApi } from '@/services/api'
import type {
  AIContentItemView,
  CloudGameRecordView,
  QuestionnaireResponseView,
  SessionSlotView,
  SessionSortField,
  SessionsQueryParams,
  SortOrder,
  TicketingSession,
  TicketingSessionDetail,
} from '@/types/ticketing'

const { RangePicker } = DatePicker
const { Text, Link } = Typography

const DEFAULT_QUERY: SessionsQueryParams = {
  page: 1,
  page_size: 10,
  sort_by: 'session_datetime',
  sort_order: 'desc',
}

const STATUS_META: Record<string, { label: string; color: string }> = {
  available: { label: '可预订', color: 'blue' },
  locked: { label: '已锁定', color: 'orange' },
  full: { label: '已满员', color: 'red' },
  in_progress: { label: '进行中', color: 'cyan' },
  completed: { label: '已完成', color: 'green' },
  cancelled: { label: '已取消', color: 'default' },
}

const AI_CONTENT_TYPE_META: Record<string, { label: string; color: string }> = {
  player_story: { label: '玩家故事', color: 'blue' },
  love_fragment_preset: { label: '爱情碎片(预设)', color: 'pink' },
  love_fragment_dynamic: { label: '爱情碎片(动态)', color: 'magenta' },
  family_fragment: { label: '家人碎片', color: 'green' },
  actor_script: { label: '演员脚本', color: 'purple' },
  actor_dialogue: { label: '演员对话', color: 'orange' },
}

const SORT_FIELD_OPTIONS: Array<{ value: SessionSortField; label: string }> = [
  { value: 'session_datetime', label: '场次时间' },
  { value: 'has_reported_data', label: '是否有槽位写入' },
  { value: 'has_cloud_report', label: '是否有云端落库' },
  { value: 'has_image', label: '是否有图片' },
  { value: 'has_ai_content', label: '是否有 AI 内容' },
  { value: 'has_code_word', label: '是否有暗号' },
  { value: 'reported_slot_count', label: '槽位写入数' },
  { value: 'cloud_report_slot_count', label: '云端落库槽位数' },
  { value: 'image_slot_count', label: '图片槽位数' },
  { value: 'raw_cloud_record_count', label: '原始云端记录数' },
  { value: 'raw_player_summary_report_count', label: '云端总结报告数' },
  { value: 'questionnaire_response_count', label: '问卷响应数' },
  { value: 'ai_generated_response_count', label: 'AI 回填响应数' },
  { value: 'ai_content_item_count', label: 'AI 内容项数' },
  { value: 'code_word_response_count', label: '暗号响应数' },
  { value: 'code_word_item_count', label: '暗号项数' },
  { value: 'latest_cloud_synced_at', label: '最新云端同步时间' },
  { value: 'latest_report_version', label: '最新 report_version' },
  { value: 'latest_schema_version', label: '最新 schema_version' },
  { value: 'world_instance_id', label: 'world_instance_id' },
  { value: 'world_status', label: 'world_status' },
  { value: 'updated_at', label: '更新时间' },
  { value: 'created_at', label: '创建时间' },
  { value: 'session_id', label: '场次业务ID' },
  { value: 'title', label: '标题' },
  { value: 'session_type', label: '场次类型' },
  { value: 'script_title', label: '剧本名' },
  { value: 'company_name', label: '商户名' },
  { value: 'venue_name', label: '场馆名' },
  { value: 'room_name', label: '房间名' },
  { value: 'status', label: '状态' },
  { value: 'current_players', label: '当前人数' },
  { value: 'max_players', label: '最大人数' },
  { value: 'slot_count', label: '总槽位数' },
  { value: 'total_price', label: '总价' },
  { value: 'vip_only', label: '是否 VIP' },
  { value: 'session_date', label: '场次日期' },
  { value: 'start_time', label: '开始时间' },
  { value: 'end_time', label: '结束时间' },
  { value: 'presale_start_at', label: '预售开始' },
  { value: 'presale_end_at', label: '预售结束' },
  { value: 'id', label: '数据库 ID' },
]

const SORT_ORDER_OPTIONS: Array<{ value: SortOrder; label: string }> = [
  { value: 'desc', label: '倒序' },
  { value: 'asc', label: '正序' },
]

function formatDateTime(value?: string | null, format = 'YYYY-MM-DD HH:mm:ss') {
  if (!value) return '-'
  return dayjs(value).isValid() ? dayjs(value).format(format) : value
}

function formatMoney(value?: number | null) {
  if (value === null || value === undefined) return '-'
  return `¥${(value / 100).toFixed(2)}`
}

function renderJson(value: unknown) {
  const isEmptyObject =
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value as Record<string, unknown>).length === 0

  if (
    value === null ||
    value === undefined ||
    value === '' ||
    isEmptyObject ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return <Text type="secondary">空</Text>
  }

  const content = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  return (
    <pre
      style={{
        margin: 0,
        padding: 12,
        borderRadius: 8,
        background: '#0f172a',
        color: '#e2e8f0',
        fontSize: 12,
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowX: 'auto',
      }}
    >
      {content}
    </pre>
  )
}

function hasSlotReport(slot: SessionSlotView) {
  const playRecord = slot.play_record
  const playRecordSummary = slot.play_record_summary

  const hasPlayRecord =
    !!playRecord &&
    typeof playRecord === 'object' &&
    !Array.isArray(playRecord) &&
    Object.keys(playRecord as Record<string, unknown>).length > 0
  const hasSummary =
    !!playRecordSummary &&
    typeof playRecordSummary === 'object' &&
    !Array.isArray(playRecordSummary) &&
    Object.keys(playRecordSummary as Record<string, unknown>).length > 0

  return hasPlayRecord || hasSummary
}

function getSessionAuditMeta(record: TicketingSession) {
  const hasReportedData = !!record.has_reported_data
  const hasCloudReport = !!record.has_cloud_report
  const hasImage = !!record.has_image

  if (!hasReportedData && !hasCloudReport && !hasImage) {
    return { label: '未上报', color: 'default' }
  }
  if (hasReportedData && hasCloudReport && hasImage) {
    return { label: '链路已覆盖', color: 'green' }
  }
  return { label: '待核对', color: 'orange' }
}

function renderAIContentType(item: AIContentItemView) {
  const meta = AI_CONTENT_TYPE_META[item.content_type || '']
  if (!meta) {
    return <Tag>{item.content_type || item.content_key}</Tag>
  }
  return <Tag color={meta.color}>{meta.label}</Tag>
}

const SessionDataViewer: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [items, setItems] = useState<TicketingSession[]>([])
  const [detail, setDetail] = useState<TicketingSessionDetail | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [query, setQuery] = useState<SessionsQueryParams>(DEFAULT_QUERY)
  const [pagination, setPagination] = useState({
    current: DEFAULT_QUERY.page || 1,
    pageSize: DEFAULT_QUERY.page_size || 10,
    total: 0,
  })

  const loadList = async (nextQuery?: SessionsQueryParams) => {
    const merged = { ...query, ...nextQuery }
    setLoading(true)
    try {
      const response = await sessionDataApi.getSessions(merged)
      setItems(response.data.items || [])
      setQuery(merged)
      setPagination({
        current: response.data.page || merged.page || 1,
        pageSize: response.data.page_size || merged.page_size || 10,
        total: response.data.total || 0,
      })
    } catch (error: any) {
      message.error(error.message || '加载场次数据失败')
    } finally {
      setLoading(false)
    }
  }

  const loadDetail = async (record: TicketingSession) => {
    setDrawerOpen(true)
    setDetailLoading(true)
    try {
      const response = await sessionDataApi.getSessionDetail(record.id)
      setDetail(response.data)
    } catch (error: any) {
      message.error(error.message || '加载场次详情失败')
      setDrawerOpen(false)
    } finally {
      setDetailLoading(false)
    }
  }

  useEffect(() => {
    void loadList(DEFAULT_QUERY)
  }, [])

  const columns: ColumnsType<TicketingSession> = [
    {
      title: '场次',
      key: 'session',
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.session_id}</Text>
          <Text type="secondary">DB ID: {record.id}</Text>
          <Text type="secondary">{record.uuid}</Text>
        </Space>
      ),
    },
    {
      title: '标题 / 剧本',
      key: 'title',
      width: 260,
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Text strong>{record.title}</Text>
          <Space wrap size={6}>
            <Tag>{record.script_title || record.script?.title || '-'}</Tag>
            {record.session_type ? <Tag color="purple">{record.session_type}</Tag> : null}
            {record.vip_only ? <Tag color="gold">VIP</Tag> : null}
          </Space>
        </Space>
      ),
    },
    {
      title: '日期时间',
      key: 'datetime',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{formatDateTime(record.session_date, 'YYYY-MM-DD')}</Text>
          <Text type="secondary">
            {record.start_time?.slice(0, 5)} - {record.end_time?.slice(0, 5)}
          </Text>
        </Space>
      ),
    },
    {
      title: '场馆 / 房间',
      key: 'venue',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.venue_name}</Text>
          <Text type="secondary">{record.room_name}</Text>
        </Space>
      ),
    },
    {
      title: '人数',
      key: 'players',
      width: 110,
      render: (_, record) => `${record.current_players}/${record.max_players}`,
    },
    {
      title: 'World',
      key: 'world',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.world_instance_id || '-'}</Text>
          <Text type="secondary">{record.world_status || '无 world_status'}</Text>
        </Space>
      ),
    },
    {
      title: 'AI 核对',
      key: 'ai_verification',
      width: 340,
      render: (_, record) => {
        const auditMeta = getSessionAuditMeta(record)
        return (
          <Space direction="vertical" size={4}>
            <Space wrap size={[4, 4]}>
              <Tag color={auditMeta.color}>{auditMeta.label}</Tag>
              {record.has_reported_data ? <Tag color="blue">槽位写入</Tag> : <Tag>无槽位写入</Tag>}
              {record.has_cloud_report ? <Tag color="cyan">云端落库</Tag> : <Tag>无云端落库</Tag>}
              {record.has_image ? <Tag color="green">有图片</Tag> : <Tag>无图片</Tag>}
              {record.has_ai_content ? <Tag color="magenta">AI 内容</Tag> : null}
              {record.has_code_word ? <Tag color="gold">暗号</Tag> : null}
            </Space>
            <Text type="secondary">
              槽位写入 {record.reported_slot_count ?? 0} / 云端 {record.cloud_report_slot_count ?? 0} / 图片{' '}
              {record.image_slot_count ?? 0}
            </Text>
            <Text type="secondary">
              原始云端 {record.raw_cloud_record_count ?? 0} / 总结报告{' '}
              {record.raw_player_summary_report_count ?? 0} / 问卷 {record.questionnaire_response_count ?? 0}
            </Text>
            <Text type="secondary">
              AI 内容 {record.ai_content_item_count ?? 0} / AI 回填响应{' '}
              {record.ai_generated_response_count ?? 0} / 暗号 {record.code_word_item_count ?? 0}
            </Text>
            <Text type="secondary">
              版本 {record.latest_report_version || '-'} / {record.latest_schema_version || '-'}
            </Text>
          </Space>
        )
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => {
        const meta = STATUS_META[status] || { label: status, color: 'default' }
        return <Tag color={meta.color}>{meta.label}</Tag>
      },
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (value?: string) => formatDateTime(value),
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button type="text" icon={<EyeOutlined />} onClick={() => loadDetail(record)}>
          查看
        </Button>
      ),
    },
  ]

  const slotColumns: ColumnsType<SessionSlotView> = [
    {
      title: '槽位',
      key: 'slot',
      width: 90,
      render: (_, slot) => slot.slot_number ?? '-',
    },
    {
      title: '角色',
      dataIndex: 'character_name',
      key: 'character_name',
      width: 180,
    },
    {
      title: '绑定用户',
      key: 'user',
      width: 160,
      render: (_, slot) => slot.bound_user_nickname || '-',
    },
    {
      title: '状态',
      key: 'slot_status',
      width: 120,
      render: (_, slot) => {
        const status = slot.slot_status || '-'
        const meta = STATUS_META[status] || { label: status, color: 'default' }
        return <Tag color={meta.color}>{meta.label}</Tag>
      },
    },
    {
      title: '截图',
      key: 'image',
      width: 100,
      render: (_, slot) => (slot.play_record_image_url ? <Tag color="green">有</Tag> : <Tag>无</Tag>),
    },
    {
      title: 'AI 上报',
      key: 'ai_status',
      width: 220,
      render: (_, slot) => (
        <Space wrap size={[4, 4]}>
          {hasSlotReport(slot) ? <Tag color="blue">槽位写入</Tag> : <Tag>无槽位写入</Tag>}
          {slot.has_cloud_report ? <Tag color="cyan">云端落库</Tag> : <Tag>无云端落库</Tag>}
          {slot.play_record_image_url ? <Tag color="green">有图片</Tag> : <Tag>无图片</Tag>}
        </Space>
      ),
    },
    {
      title: '更新时间',
      key: 'updated_at',
      width: 180,
      render: (_, slot) => formatDateTime(slot.slot_updated_at),
    },
  ]

  const cloudRecordColumns: ColumnsType<CloudGameRecordView> = [
    {
      title: 'record_type',
      dataIndex: 'record_type',
      key: 'record_type',
      width: 180,
      render: (value: string) => (
        <Tag color={value === 'player_summary_report' ? 'cyan' : 'default'}>{value}</Tag>
      ),
    },
    {
      title: '玩家',
      key: 'player',
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.player_nickname || '-'}</Text>
          <Text type="secondary">
            player_id: {record.player_id}
            {record.player_phone ? ` / ${record.player_phone}` : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: 'character_id',
      dataIndex: 'character_id',
      key: 'character_id',
      width: 160,
      render: (value?: string | null) => value || '-',
    },
    {
      title: 'record_key',
      dataIndex: 'record_key',
      key: 'record_key',
      width: 260,
      ellipsis: true,
      render: (value: string) => <Text code>{value}</Text>,
    },
    {
      title: '版本',
      key: 'version',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.report_version || '-'}</Text>
          <Text type="secondary">{record.schema_version || '-'}</Text>
        </Space>
      ),
    },
    {
      title: 'synced_at',
      dataIndex: 'synced_at',
      key: 'synced_at',
      width: 180,
      render: (value?: string | null) => formatDateTime(value),
    },
  ]

  const aiContentColumns: ColumnsType<AIContentItemView> = [
    {
      title: '内容键',
      dataIndex: 'content_key',
      key: 'content_key',
      width: 180,
      render: (value: string) => <Text code>{value}</Text>,
    },
    {
      title: '类型',
      key: 'content_type',
      width: 220,
      render: (_, item) => (
        <Space wrap size={[4, 4]}>
          {renderAIContentType(item)}
          {item.sub_type ? <Tag>{item.sub_type}</Tag> : null}
        </Space>
      ),
    },
    {
      title: '摘要',
      dataIndex: 'content_summary',
      key: 'content_summary',
      render: (value?: string | null) => value || '-',
    },
    {
      title: 'generated_at',
      dataIndex: 'generated_at',
      key: 'generated_at',
      width: 180,
      render: (value?: string | null) => formatDateTime(value),
    },
  ]

  const questionnaireColumns: ColumnsType<QuestionnaireResponseView> = [
    {
      title: '问卷',
      key: 'questionnaire',
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.questionnaire_key}</Text>
          <Text type="secondary">ID: {record.id}</Text>
        </Space>
      ),
    },
    {
      title: '玩家',
      key: 'user',
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.user_nickname || `user:${record.user_id}`}</Text>
          <Text type="secondary">{record.user_phone || `user_id: ${record.user_id}`}</Text>
        </Space>
      ),
    },
    {
      title: '填写状态',
      dataIndex: 'is_completed',
      key: 'is_completed',
      width: 110,
      render: (value: boolean) => (value ? <Tag color="green">已完成</Tag> : <Tag>未完成</Tag>),
    },
    {
      title: 'AI / 暗号',
      key: 'ai_status',
      width: 260,
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Space wrap size={[4, 4]}>
            {record.has_ai_content ? <Tag color="magenta">有 AI 内容</Tag> : <Tag>无 AI 内容</Tag>}
            {record.has_code_word ? <Tag color="gold">有暗号</Tag> : <Tag>无暗号</Tag>}
          </Space>
          <Text type="secondary">
            内容项 {record.ai_content_items?.length || 0} / raw AI{' '}
            {record.ai_generation_result ? '1' : '0'}
          </Text>
        </Space>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (value?: string | null) => formatDateTime(value),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="只读场次透视页"
        description="这里直接展示 ticketing_sessions、session_slots、game_records_sync、questionnaire_responses 以及 AI 上报核对字段，不提供编辑能力。可按是否有槽位写入、是否有云端落库、是否有图片、是否有 AI 内容、是否有暗号排序，重点核查原始落库 JSON 和我们加工回写到数据库的字段。"
      />

      <Card style={{ marginBottom: 16 }}>
        <Space wrap size={12}>
          <Input
            allowClear
            placeholder="按 session_id 搜索"
            style={{ width: 220 }}
            value={query.session_id}
            onChange={(event) => setQuery((prev) => ({ ...prev, session_id: event.target.value || undefined }))}
            onPressEnter={() => loadList({ ...query, page: 1 })}
          />
          <Input
            allowClear
            placeholder="按标题关键词搜索"
            style={{ width: 220 }}
            value={query.title}
            onChange={(event) => setQuery((prev) => ({ ...prev, title: event.target.value || undefined }))}
            onPressEnter={() => loadList({ ...query, page: 1 })}
          />
          <Select
            allowClear
            placeholder="状态"
            style={{ width: 160 }}
            value={query.status}
            onChange={(value) => setQuery((prev) => ({ ...prev, status: value }))}
            options={Object.entries(STATUS_META).map(([value, meta]) => ({
              value,
              label: meta.label,
            }))}
          />
          <RangePicker
            value={
              query.start_date && query.end_date
                ? [dayjs(query.start_date, 'YYYY-MM-DD'), dayjs(query.end_date, 'YYYY-MM-DD')]
                : null
            }
            onChange={(dates) => {
              if (!dates || !dates[0] || !dates[1]) {
                setQuery((prev) => ({ ...prev, start_date: undefined, end_date: undefined }))
                return
              }
              setQuery((prev) => ({
                ...prev,
                start_date: dates[0].format('YYYY-MM-DD'),
                end_date: dates[1].format('YYYY-MM-DD'),
              }))
            }}
          />
          <Select
            placeholder="排序字段"
            style={{ width: 180 }}
            value={query.sort_by}
            onChange={(value) =>
              setQuery((prev) => ({ ...prev, sort_by: value as SessionSortField }))
            }
            options={SORT_FIELD_OPTIONS}
          />
          <Select
            placeholder="排序方向"
            style={{ width: 120 }}
            value={query.sort_order}
            onChange={(value) =>
              setQuery((prev) => ({ ...prev, sort_order: value as SortOrder }))
            }
            options={SORT_ORDER_OPTIONS}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={() => loadList({ ...query, page: 1 })}>
            搜索
          </Button>
          <Button
            onClick={() => {
              setQuery(DEFAULT_QUERY)
              void loadList(DEFAULT_QUERY)
            }}
          >
            重置
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => loadList(query)}>
            刷新
          </Button>
        </Space>
      </Card>

      <Card
        title="ticketing_sessions 列表"
        bordered={false}
        extra={
          <Text type="secondary">
            当前排序: {SORT_FIELD_OPTIONS.find((item) => item.value === query.sort_by)?.label || '数据库 ID'} /{' '}
            {query.sort_order === 'asc' ? '正序' : '倒序'}
          </Text>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={items}
          scroll={{ x: 1780 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          onChange={(pager) => {
            void loadList({
              ...query,
              page: pager.current || 1,
              page_size: pager.pageSize || pagination.pageSize,
            })
          }}
        />
      </Card>

      <Drawer
        title={detail ? `场次详情: ${detail.session_id}` : '场次详情'}
        width={1200}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setDetail(null)
        }}
      >
        {detailLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <Spin />
          </div>
        ) : !detail ? (
          <Empty description="未加载到详情" />
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card size="small" title="ticketing_sessions 主记录">
              <Descriptions bordered column={3} size="small">
                <Descriptions.Item label="DB ID">{detail.id}</Descriptions.Item>
                <Descriptions.Item label="session_id">{detail.session_id}</Descriptions.Item>
                <Descriptions.Item label="uuid">{detail.uuid}</Descriptions.Item>
                <Descriptions.Item label="title">{detail.title}</Descriptions.Item>
                <Descriptions.Item label="session_type">{detail.session_type || '-'}</Descriptions.Item>
                <Descriptions.Item label="status">
                  <Tag color={(STATUS_META[detail.status] || { color: 'default' }).color}>
                    {(STATUS_META[detail.status] || { label: detail.status }).label}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="script">{detail.script_title || detail.script?.title}</Descriptions.Item>
                <Descriptions.Item label="venue">{detail.venue_name}</Descriptions.Item>
                <Descriptions.Item label="room">{detail.room_name}</Descriptions.Item>
                <Descriptions.Item label="session_date">{formatDateTime(detail.session_date, 'YYYY-MM-DD')}</Descriptions.Item>
                <Descriptions.Item label="start_time">{detail.start_time}</Descriptions.Item>
                <Descriptions.Item label="end_time">{detail.end_time}</Descriptions.Item>
                <Descriptions.Item label="world_instance_id">{detail.world_instance_id || '-'}</Descriptions.Item>
                <Descriptions.Item label="world_status">{detail.world_status || '-'}</Descriptions.Item>
                <Descriptions.Item label="AI 核对">
                  <Tag color={getSessionAuditMeta(detail).color}>{getSessionAuditMeta(detail).label}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="duration">{detail.duration} 分钟</Descriptions.Item>
                <Descriptions.Item label="players">
                  {detail.current_players}/{detail.max_players}
                </Descriptions.Item>
                <Descriptions.Item label="vip_only">{detail.vip_only ? 'true' : 'false'}</Descriptions.Item>
                <Descriptions.Item label="presale_start_at">{formatDateTime(detail.presale_start_at)}</Descriptions.Item>
                <Descriptions.Item label="presale_end_at">{formatDateTime(detail.presale_end_at)}</Descriptions.Item>
                <Descriptions.Item label="lock_code">{detail.lock_code || '-'}</Descriptions.Item>
                <Descriptions.Item label="captain_user_id">{detail.captain_user_id ?? '-'}</Descriptions.Item>
                <Descriptions.Item label="captain_slot_id">{detail.captain_slot_id ?? '-'}</Descriptions.Item>
                <Descriptions.Item label="created_at">{formatDateTime(detail.created_at)}</Descriptions.Item>
                <Descriptions.Item label="updated_at">{formatDateTime(detail.updated_at)}</Descriptions.Item>
                <Descriptions.Item label="group_qr_code_url" span={3}>
                  {detail.group_qr_code_url ? (
                    <Link href={detail.group_qr_code_url} target="_blank">
                      {detail.group_qr_code_url}
                    </Link>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="description" span={3}>
                  {detail.description || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size="small" title="AI 上报核对字段">
              <Descriptions bordered column={4} size="small">
                <Descriptions.Item label="slot_count">{detail.slot_count ?? 0}</Descriptions.Item>
                <Descriptions.Item label="reported_slot_count">{detail.reported_slot_count ?? 0}</Descriptions.Item>
                <Descriptions.Item label="cloud_report_slot_count">
                  {detail.cloud_report_slot_count ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label="image_slot_count">{detail.image_slot_count ?? 0}</Descriptions.Item>
                <Descriptions.Item label="raw_cloud_record_count">
                  {detail.raw_cloud_record_count ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label="raw_player_summary_report_count">
                  {detail.raw_player_summary_report_count ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label="questionnaire_response_count">
                  {detail.questionnaire_response_count ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label="ai_generated_response_count">
                  {detail.ai_generated_response_count ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label="ai_content_item_count">
                  {detail.ai_content_item_count ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label="code_word_response_count">
                  {detail.code_word_response_count ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label="code_word_item_count">
                  {detail.code_word_item_count ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label="has_reported_data">
                  {detail.has_reported_data ? 'true' : 'false'}
                </Descriptions.Item>
                <Descriptions.Item label="has_cloud_report">
                  {detail.has_cloud_report ? 'true' : 'false'}
                </Descriptions.Item>
                <Descriptions.Item label="has_image">{detail.has_image ? 'true' : 'false'}</Descriptions.Item>
                <Descriptions.Item label="has_ai_content">
                  {detail.has_ai_content ? 'true' : 'false'}
                </Descriptions.Item>
                <Descriptions.Item label="has_code_word">
                  {detail.has_code_word ? 'true' : 'false'}
                </Descriptions.Item>
                <Descriptions.Item label="latest_cloud_synced_at">
                  {formatDateTime(detail.latest_cloud_synced_at)}
                </Descriptions.Item>
                <Descriptions.Item label="latest_report_version">
                  {detail.latest_report_version || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="latest_schema_version">
                  {detail.latest_schema_version || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size="small" title="价格与折扣字段">
              <Descriptions bordered column={3} size="small">
                <Descriptions.Item label="unit_price">{formatMoney(detail.unit_price)}</Descriptions.Item>
                <Descriptions.Item label="total_price">{formatMoney(detail.total_price)}</Descriptions.Item>
                <Descriptions.Item label="vip_unit_price">{formatMoney(detail.vip_unit_price)}</Descriptions.Item>
                <Descriptions.Item label="vip_total_price">{formatMoney(detail.vip_total_price)}</Descriptions.Item>
                <Descriptions.Item label="discount_unit_price">{formatMoney(detail.discount_unit_price)}</Descriptions.Item>
                <Descriptions.Item label="discount_total_price">{formatMoney(detail.discount_total_price)}</Descriptions.Item>
                <Descriptions.Item label="vip_discount_unit_price">{formatMoney(detail.vip_discount_unit_price)}</Descriptions.Item>
                <Descriptions.Item label="vip_discount_total_price">{formatMoney(detail.vip_discount_total_price)}</Descriptions.Item>
                <Descriptions.Item label="discount_enabled">{detail.discount_enabled ? 'true' : 'false'}</Descriptions.Item>
                <Descriptions.Item label="discount_start_at">{formatDateTime(detail.discount_start_at)}</Descriptions.Item>
                <Descriptions.Item label="discount_end_at">{formatDateTime(detail.discount_end_at)}</Descriptions.Item>
                <Descriptions.Item label="vip_discount_enabled">{detail.vip_discount_enabled ? 'true' : 'false'}</Descriptions.Item>
                <Descriptions.Item label="vip_discount_start_at">{formatDateTime(detail.vip_discount_start_at)}</Descriptions.Item>
                <Descriptions.Item label="vip_discount_end_at">{formatDateTime(detail.vip_discount_end_at)}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card size="small" title="ticketing_sessions.extra_metadata">
                  {renderJson(detail.extra_metadata)}
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card size="small" title="group_qr_code_url 预览">
                  {detail.group_qr_code_url ? (
                    <Image
                      src={detail.group_qr_code_url}
                      alt="group_qr_code_url"
                      style={{ maxHeight: 280, objectFit: 'contain' }}
                    />
                  ) : (
                    <Empty description="无图片" />
                  )}
                </Card>
              </Col>
            </Row>

            <Card size="small" title={`session_slots (${detail.slots?.length || 0})`}>
              <Table
                rowKey={(slot) => slot.slot_id ?? slot.slots_uuid}
                size="small"
                pagination={false}
                dataSource={detail.slots || []}
                columns={slotColumns}
                expandable={{
                  defaultExpandAllRows: true,
                  expandedRowRender: (slot) => (
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                      <Descriptions bordered column={3} size="small">
                        <Descriptions.Item label="slot_id">{slot.slot_id ?? '-'}</Descriptions.Item>
                        <Descriptions.Item label="slot_number">{slot.slot_number ?? '-'}</Descriptions.Item>
                        <Descriptions.Item label="slot_status">{slot.slot_status || '-'}</Descriptions.Item>
                        <Descriptions.Item label="character_id">{slot.character_id ?? '-'}</Descriptions.Item>
                        <Descriptions.Item label="characters_uuid">{slot.characters_uuid}</Descriptions.Item>
                        <Descriptions.Item label="bound_user_id">{slot.bound_user_id ?? slot.bound_user_db_id ?? '-'}</Descriptions.Item>
                        <Descriptions.Item label="bound_ticket_id">{slot.bound_ticket_id ?? '-'}</Descriptions.Item>
                        <Descriptions.Item label="bound_at">{formatDateTime(slot.bound_at)}</Descriptions.Item>
                        <Descriptions.Item label="locked_at">{formatDateTime(slot.locked_at)}</Descriptions.Item>
                        <Descriptions.Item label="slot_created_at">{formatDateTime(slot.slot_created_at)}</Descriptions.Item>
                        <Descriptions.Item label="slot_updated_at">{formatDateTime(slot.slot_updated_at)}</Descriptions.Item>
                        <Descriptions.Item label="bound_user_phone">{slot.bound_user_phone || '-'}</Descriptions.Item>
                        <Descriptions.Item label="cloud_report_count">{slot.cloud_report_count ?? 0}</Descriptions.Item>
                        <Descriptions.Item label="has_cloud_report">
                          {slot.has_cloud_report ? 'true' : 'false'}
                        </Descriptions.Item>
                        <Descriptions.Item label="latest_cloud_synced_at">
                          {formatDateTime(slot.latest_cloud_synced_at)}
                        </Descriptions.Item>
                        <Descriptions.Item label="latest_report_version">
                          {slot.latest_report_version || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="latest_schema_version">
                          {slot.latest_schema_version || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="latest_cloud_record_id">
                          {slot.latest_cloud_record_id ?? '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="latest_cloud_record_key" span={3}>
                          {slot.latest_cloud_record_key || '-'}
                        </Descriptions.Item>
                      </Descriptions>

                      <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                          <Card size="small" title="session_slots.play_record_image_url">
                            {slot.play_record_image_url ? (
                              <Image
                                src={slot.play_record_image_url}
                                alt={`slot-${slot.slot_number || slot.slot_id}`}
                                style={{ maxHeight: 320, objectFit: 'contain' }}
                              />
                            ) : (
                              <Empty description="无图片" />
                            )}
                          </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Card size="small" title="session_slots.extra_metadata">
                            {renderJson(slot.slot_extra_metadata)}
                          </Card>
                        </Col>
                      </Row>

                      <Card size="small" title="session_slots.play_record_summary">
                        {renderJson(slot.play_record_summary)}
                      </Card>

                      <Card size="small" title="session_slots.play_record">
                        {renderJson(slot.play_record)}
                      </Card>

                      <Card size="small" title="game_records_sync.latest record_data">
                        {renderJson(slot.latest_cloud_record_data)}
                      </Card>
                    </Space>
                  ),
                }}
              />
            </Card>

            <Card size="small" title={`game_records_sync 原始记录 (${detail.cloud_game_records?.length || 0})`}>
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={detail.cloud_game_records || []}
                columns={cloudRecordColumns}
                scroll={{ x: 1180 }}
                expandable={{
                  expandedRowRender: (record) => (
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                      <Descriptions bordered column={3} size="small">
                        <Descriptions.Item label="id">{record.id}</Descriptions.Item>
                        <Descriptions.Item label="merchant_id">{record.merchant_id}</Descriptions.Item>
                        <Descriptions.Item label="venue_id">{record.venue_id || '-'}</Descriptions.Item>
                        <Descriptions.Item label="world_id">{record.world_id}</Descriptions.Item>
                        <Descriptions.Item label="world_instance_id">
                          {record.world_instance_id ?? '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="subject_type">{record.subject_type}</Descriptions.Item>
                        <Descriptions.Item label="player_id">{record.player_id}</Descriptions.Item>
                        <Descriptions.Item label="player_nickname">
                          {record.player_nickname || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="player_phone">
                          {record.player_phone || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="character_id">{record.character_id || '-'}</Descriptions.Item>
                        <Descriptions.Item label="record_type">{record.record_type}</Descriptions.Item>
                        <Descriptions.Item label="report_version">
                          {record.report_version || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="schema_version">
                          {record.schema_version || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="synced_at">{formatDateTime(record.synced_at)}</Descriptions.Item>
                        <Descriptions.Item label="created_at">{formatDateTime(record.created_at)}</Descriptions.Item>
                        <Descriptions.Item label="updated_at">{formatDateTime(record.updated_at)}</Descriptions.Item>
                        <Descriptions.Item label="record_key" span={3}>
                          <Text code>{record.record_key}</Text>
                        </Descriptions.Item>
                      </Descriptions>

                      <Card size="small" title="game_records_sync.record_data">
                        {renderJson(record.record_data)}
                      </Card>
                    </Space>
                  ),
                }}
              />
            </Card>

            <Card
              size="small"
              title={`questionnaire_responses (${detail.questionnaire_responses?.length || 0})`}
            >
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={detail.questionnaire_responses || []}
                columns={questionnaireColumns}
                scroll={{ x: 1100 }}
                expandable={{
                  expandedRowRender: (response) => (
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                      <Descriptions bordered column={3} size="small">
                        <Descriptions.Item label="id">{response.id}</Descriptions.Item>
                        <Descriptions.Item label="script_id">{response.script_id}</Descriptions.Item>
                        <Descriptions.Item label="session_id">{response.session_id}</Descriptions.Item>
                        <Descriptions.Item label="user_id">{response.user_id}</Descriptions.Item>
                        <Descriptions.Item label="user_nickname">
                          {response.user_nickname || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="user_phone">
                          {response.user_phone || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="questionnaire_key">
                          {response.questionnaire_key}
                        </Descriptions.Item>
                        <Descriptions.Item label="is_completed">
                          {response.is_completed ? 'true' : 'false'}
                        </Descriptions.Item>
                        <Descriptions.Item label="ai_generated_at">
                          {formatDateTime(response.ai_generated_at)}
                        </Descriptions.Item>
                        <Descriptions.Item label="submitted_at">
                          {formatDateTime(response.submitted_at)}
                        </Descriptions.Item>
                        <Descriptions.Item label="updated_at">
                          {formatDateTime(response.updated_at)}
                        </Descriptions.Item>
                        <Descriptions.Item label="has_ai_content">
                          {response.has_ai_content ? 'true' : 'false'}
                        </Descriptions.Item>
                        <Descriptions.Item label="has_code_word">
                          {response.has_code_word ? 'true' : 'false'}
                        </Descriptions.Item>
                      </Descriptions>

                      <Card
                        size="small"
                        title={`提取后的 AI 内容项 (${response.ai_content_items?.length || 0})`}
                      >
                        {response.ai_content_items?.length ? (
                          <Table
                            rowKey={(item) => `${item.content_key}-${item.generated_at || 'na'}`}
                            size="small"
                            pagination={false}
                            dataSource={response.ai_content_items}
                            columns={aiContentColumns}
                            expandable={{
                              expandedRowRender: (item) => (
                                <Card size="small" title="content_data">
                                  {renderJson(item.content_data)}
                                </Card>
                              ),
                            }}
                          />
                        ) : (
                          <Empty description="无 AI 内容项" />
                        )}
                      </Card>

                      <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                          <Card size="small" title="提取后的暗号">
                            {response.code_word ? renderJson(response.code_word) : <Empty description="无暗号" />}
                          </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Card size="small" title="questionnaire_responses.extra_metadata">
                            {renderJson(response.extra_metadata)}
                          </Card>
                        </Col>
                      </Row>

                      <Card size="small" title="questionnaire_responses.answers">
                        {renderJson(response.answers)}
                      </Card>

                      <Card size="small" title="questionnaire_responses.ai_generation_result">
                        {renderJson(response.ai_generation_result)}
                      </Card>
                    </Space>
                  ),
                }}
              />
            </Card>
          </Space>
        )}
      </Drawer>
    </div>
  )
}

export default SessionDataViewer
